import {
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import { portfolioApi } from "../../services/portfolioApi";

interface BulkDeleteButtonProps {
  portfolioId: string;
  selectedItems: Array<{
    id: string;
    name: string;
    type: string;
  }>;
  onSuccess?: () => void;
}

const BulkDeleteButton: React.FC<BulkDeleteButtonProps> = ({
  portfolioId,
  selectedItems,
  onSuccess,
}) => {
  const { showNotification } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleBulkDelete = async () => {
    if (!portfolioId || selectedItems.length === 0) return;

    try {
      setLoading(true);

      // Extract item IDs for bulk delete (convert to integers as backend expects)
      const itemIds = selectedItems.map((item) => parseInt(item.id, 10));

      await portfolioApi.bulkDeleteItems(portfolioId, { item_ids: itemIds });

      showNotification(
        `Successfully removed ${selectedItems.length} item${selectedItems.length !== 1 ? "s" : ""} from portfolio`,
        "success"
      );

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to remove items from portfolio. Please try again.";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        startIcon={<DeleteIcon />}
        variant="outlined"
        size="small"
        color="error"
        disabled={selectedItems.length === 0}
        onClick={handleOpen}
      >
        Delete ({selectedItems.length})
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="error" />
            Remove {selectedItems.length} Items from Portfolio
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This action will permanently remove the selected items from this
              portfolio. This cannot be undone.
            </Typography>

            {/* Selected items summary */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Items to be removed ({selectedItems.length}):
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {selectedItems.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.name}
                    size="small"
                    variant="outlined"
                    color="error"
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            color="error"
            disabled={selectedItems.length === 0 || loading}
          >
            {loading
              ? "Removing..."
              : `Remove ${selectedItems.length} Item${selectedItems.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkDeleteButton;
